import { htmlEscape } from '@edicom/common/string/htmlEscape';
import { renderChips } from './renderChips';

/**
 * Devuelve una función que pinta el html de 1 chips.
 * En función de los parametros del view, pintara el chips rojo para cargo, o verde para descuento, etc...
 *
 * @example de uso en el view:
 *  metadata: {
 *    template: {
 *      isDiscount: "CALDTO === 'A'",
 *      percentage: 'PERCENT',
 *      total: 'TOTAL',
 *    },
 *  },
 * @param {Object} viewField
 * @param {Object} engineApi
 * @returns {function}
 */
export function rendererDiscountAndOrTaxes(viewField, engineApi) {
  const keys = viewField.metadata.template;
  const isDiscount = viewField.metadata.template.isDiscount ? parse.bind(null, viewField.metadata.template.isDiscount) : () => false;
  const getIcon = getIconFactory(isDiscount);
  const getTheme = getThemeFactory(isDiscount);
  const getPercent = getPercentFactory(findViewProperty(viewField, keys.percentage));
  const getTotal = getTotalFactory(findViewProperty(viewField, keys.total));
  const isNumber = num => typeof num === 'number' && !isNaN(num);
  const valueToTextQualifier = valueToText(findViewProperty(viewField, keys.qualifier));
  const valueToTextType = valueToText(findViewProperty(viewField, keys.type));

  return ({ value, index, isSelected, isEditable }) => {
    const isEmpty = !value || Object.values(value).filter(v => v !== '' && v !== undefined && v !== null).length === 0;
    if (isEmpty) {
      return '';
    }
    const icon = getIcon(value);
    const theme = getTheme(value);
    const qualifier = valueToTextQualifier(value[keys.qualifier]);
    const type = valueToTextType(value[keys.type]);
    const percentage = getPercent(value[keys.percentage]);
    const total = getTotal(value[keys.total]);
    const label = getLabel(qualifier, type, percentage, total);

    return renderChips({ index, icon, label, theme, isSelected, isEditable });
  };

  function getLabel(...args) {
    return htmlEscape(args.filter(v => v !== '').join(' '));
  }

  function getIconFactory(isDiscountFn) {
    return value => (isDiscountFn(value) ? 'circle-minus' : 'circle-plus');
  }

  function getThemeFactory(isDiscountFn) {
    return value => (isDiscountFn(value) ? 'ec-success' : 'ec-danger');
  }

  function getPercentFactory(viewProperty) {
    if (!viewProperty) {
      return () => '';
    }
    const format = engineApi.field.toString(Object.assign({ metadata: { mask: 'percent' } }, viewProperty));
    return value => {
      if (!isNumber(value)) {
        return '';
      }

      return format(value);
    };
  }

  function getTotalFactory(viewProperty) {
    if (!viewProperty) {
      return () => '';
    }

    const format = engineApi.field.toString(Object.assign({ metadata: { mask: 'currency' } }, viewProperty));

    return value => {
      if (!isNumber(value)) {
        return '';
      }

      return format(value);
    };
  }

  function valueToText(viewProperty) {
    if (!viewProperty || !viewProperty.metadata || (!viewProperty.metadata.select && !viewProperty.metadata.options)) {
      return () => '';
    }

    const values = viewProperty.metadata.select
      ? viewProperty.metadata.select
      : viewProperty.metadata.options.reduce((acc, cur) => {
          acc[cur.value] = cur.text;
          return acc;
        }, {});

    return value => values[value] || '';
  }

  function findViewProperty({ properties }, name) {
    const prop = (properties.filter(p => p.model.indexOf(name) > -1) || [])[0];
    return prop;
  }
}

function parse(expression = 'false', scope = {}) {
  return new Function(Object.keys(scope).toString(), `; return ${expression}`).apply(null, Object.values(scope));
}
